import React, { useState } from "react";
import { Typography, Box } from "@mui/material";
import CourseActivity from "./CourseActivity";
import CourseModule from "./CourseModule";
import PropTypes from "prop-types";
import * as constants from "../../constants";
import SearchBar from "../SearchBar/SearchBar";
import { useTranslation } from "react-i18next";
import CoursePlayerTabHeader from "./CoursePlayerTabHeader";
import CoursePlayerShortCourses from "./CoursePlayerShortCourses";
import Tabs from "../Tabs/Tabs";
import SwipeableViews from "react-swipeable-views";
import CoursePlayerPrintButton from "./CoursePlayerPrintButton";
import { activityTypes } from "../../constants/index";
import { sortBy } from "lodash";

const filterBySearchInput = searchInput => courseModule => {
  let match = false;
  if (
    courseModule.title.toLowerCase().indexOf(searchInput.toLowerCase()) !== -1
  ) {
    match = true;
  } else {
    courseModule.courseContentables.forEach(activity => {
      if (
        activity.title.toLowerCase().indexOf(searchInput.toLowerCase()) !== -1
      ) {
        match = true;
      }
    });
  }
  return match;
};

const CoursePlayerContentTab = ({
  modules,
  searchInput,
  showProgress,
  isLoading
}) => (
  <CoursePlayerShortCourses
    isLoading={isLoading}
    shortCourses={modules}
    searchResults={!!searchInput}
    showProgress={showProgress}
  />
);

function CoursePlayer({
  courseTitle,
  modules,
  resources,
  numActivitiesCompleted,
  totalActivities,
  onGoToActivityClick,
  passFail,
  isLoading,
  isSelfAssignedPolicy
}) {
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState(0);
  const [searchInput, setSearchInput] = useState("");

  function getActivityStatus(
    activityStatus,
    activityScore,
    activityType,
    isSelfAssignedPolicy
  ) {
    if (isSelfAssignedPolicy) {
      return null;
    } else if (
      activityStatus?.toLowerCase() === constants.statusInProgress.toLowerCase()
    ) {
      return t("coursePlayer.activityStatus.inProgress");
    } else if (
      activityStatus?.toLowerCase() === constants.statusCompleted.toLowerCase()
    ) {
      return passFail ||
        typeof activityScore !== "number" ||
        activityType === activityTypes.survey
        ? t("coursePlayer.activityStatus.completed")
        : `${Math.round(activityScore)}%`;
    }
  }

  const mapModule = isPrintVersion => (courseModule, moduleIndex) => {
    let allActivitiesCompleted = true;
    const reorderedActivities = sortBy(
      courseModule.courseContentables,
      "position"
    );
    const activities = reorderedActivities.map((activity, index) => {
      if (
        !(
          activity.status?.toLowerCase() ===
          constants.statusCompleted.toLowerCase()
        )
      ) {
        allActivitiesCompleted = false;
      }
      return (
        <CourseActivity
          title={activity.title}
          description={activity.description}
          contentableType={activity.type}
          status={activity.status}
          submittedQuiz={activity.submittedQuiz}
          searchInput={searchInput}
          onGoToActivityClick={() =>
            onGoToActivityClick(activity, courseModule.id)
          }
          key={index}
          showActivityButton={!isPrintVersion}
          activityStatusText={getActivityStatus(
            activity.status,
            activity.score,
            activity.type,
            isSelfAssignedPolicy
          )}
          isSelfAssignedPolicy={isSelfAssignedPolicy}
        />
      );
    });
    return {
      moduleComponent: (
        <CourseModule
          title={courseModule.title}
          activityList={activities}
          key={moduleIndex}
          searchInput={searchInput}
        />
      ),
      allActivitiesCompleted: allActivitiesCompleted
    };
  };

  const getModules = modules =>
    modules
      ? modules.filter(filterBySearchInput(searchInput)).map(mapModule(false))
      : [];

  const getPrintModules = () => {
    let shortCourses = currentTab === 0 ? modules : resources;
    return shortCourses && shortCourses.map(mapModule(true));
  };

  const tableOfContentsModules = getModules(modules);
  const resourcesModules = getModules(resources);

  const tableOfContentsTab = (
    <CoursePlayerContentTab
      modules={tableOfContentsModules}
      searchInput={searchInput}
      isLoading={isLoading}
      showProgress={!isSelfAssignedPolicy}
    />
  );
  const resourcesTab = (
    <CoursePlayerContentTab
      modules={resourcesModules}
      searchInput={searchInput}
      showProgress={false}
    />
  );

  const numSearchResults =
    currentTab === 0 ? tableOfContentsModules.length : resourcesModules.length;

  const tabs = [t("coursePlayer.navigationTabs.tableOfContents")];
  if (resources && resources.length > 0) {
    tabs.push(t("coursePlayer.navigationTabs.resources"));
  }

  return (
    <Box display="flex" flexDirection="column">
      <Box mt={5} mb={3} minHeight={40}>
        <Typography variant="h2" color="textSecondary">
          {courseTitle}
        </Typography>
      </Box>
      <Tabs tabs={tabs} currentTab={currentTab} onChange={setCurrentTab} />
      <Box
        display="flex"
        my={4}
        flex={1}
        justifyContent="space-between"
        alignItems="center"
      >
        <Box minWidth={{ xs: 0, sm: 400 }}>
          <SearchBar
            value=""
            filled
            placeholder={t("coursePlayer.tableOfContentsTab.search")}
            onSearchInput={setSearchInput}
            searchIconPosition="end"
          />
        </Box>
        <CoursePlayerPrintButton
          courseTitle={courseTitle}
          modules={getPrintModules()}
        />
      </Box>
      {!isLoading && modules && (
        <CoursePlayerTabHeader
          isTableOfContents={currentTab === 0}
          searchInput={searchInput}
          totalActivities={totalActivities}
          numActivitiesCompleted={isLoading ? 0 : numActivitiesCompleted}
          numSearchResults={numSearchResults}
          hideProgress={isSelfAssignedPolicy}
        />
      )}
      <SwipeableViews axis="x" index={currentTab} onChangeIndex={setCurrentTab}>
        {tableOfContentsTab}
        {resourcesTab}
      </SwipeableViews>
    </Box>
  );
}

export default CoursePlayer;

CoursePlayer.propTypes = {
  onGoToActivityClick: PropTypes.func,
  resources: PropTypes.any,
  courseTitle: PropTypes.string,
  courseType: PropTypes.string,
  numActivitiesCompleted: PropTypes.number,
  totalActivities: PropTypes.number,
  modules: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      activities: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.string,
          status: PropTypes.string,
          title: PropTypes.string
        })
      )
    })
  ),
  isSelfAssignedPolicy: PropTypes.bool
};
